import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import moment from 'moment'

import { useAuth, useMembershipRedux, useConfirmDialog } from '@hooks'
import {
    ROUTES,
    customToast,
    ORDER_HISTORY_FILTER_CATEGORY,
    ORDER_HISTORY_FILTER_CATEGORY_OPTIONS,
    ORDER_HISTORY_FILTER_TO_BACKEND_STATUS,
} from '@common'
import { Loader, OrderHistoryFilters, OrderDetailModal } from '@components'
import { ErrorTrackingAPI, membershipServices } from '@services'

export default function MemberHistoryPage() {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { isAuthenticated, memberData, isLoading } = useAuth()
    const {
        orderHistory,
        orderHistoryLoading,
        orderHistoryError,
        orderHistoryPageInfo,
        upcomingOrders,
        upcomingOrdersLoading,
        upcomingOrdersError,
        upcomingOrdersPageInfo,
        cancelOrderLoading,
        retrieveMemberOrderHistory,
        retrieveMemberUpcomingOrders,
        cancelMemberOrder,
        clearError,
    } = useMembershipRedux()
    const { confirm } = useConfirmDialog()

    const [searchTerm, setSearchTerm] = useState('')
    // Check URL query parameter for initial view type
    const initialViewType = searchParams.get('tab') === 'upcoming' ? 'Upcoming' : 'History'
    const [viewType, setViewType] = useState(initialViewType) // 'History' or 'Upcoming'
    const [categoryFilter, setCategoryFilter] = useState({
        value: ORDER_HISTORY_FILTER_CATEGORY.ALL,
        label: 'All',
    })
    const [historyOffset, setHistoryOffset] = useState(0)
    const [upcomingOffset, setUpcomingOffset] = useState(0)
    const [selectedOrder, setSelectedOrder] = useState(null)
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
    const [cancellingOrderId, setCancellingOrderId] = useState(null)
    const hasInitialLoad = useRef(false)
    const shouldScrollToTop = useRef(false)
    const LIMIT = 10

    // Default date range: today to one week ago
    const defaultEndDate = moment().format('YYYY-MM-DD')
    const defaultStartDate = moment().subtract(7, 'days').format('YYYY-MM-DD')
    const [startDate, setStartDate] = useState(defaultStartDate)
    const [endDate, setEndDate] = useState(defaultEndDate)

    useEffect(() => {
        // Only redirect if loading is complete and user is not authenticated
        // This prevents redirecting during initial authentication check
        if (!isLoading && !isAuthenticated) {
            // Check if there's a token in localStorage to avoid redirect loop
            // If token exists, wait for authentication to complete instead of redirecting
            const tokens = membershipServices.getTokens()
            if (!tokens.accessToken) {
                navigate(ROUTES.Membership.auth, { replace: true })
            }
            // If token exists but isAuthenticated is false, it means auth is still loading
            // Don't redirect, let the auth initializer handle it
        }
    }, [isAuthenticated, isLoading, navigate])

    // Initial load: fetch both order history and upcoming orders when component mounts
    useEffect(() => {
        if (isAuthenticated && !hasInitialLoad.current) {
            const status = ORDER_HISTORY_FILTER_TO_BACKEND_STATUS[categoryFilter.value] || []
            retrieveMemberOrderHistory({
                limit: LIMIT,
                offset: historyOffset,
                startDate: startDate ? moment(startDate).startOf('day').toISOString() : null,
                endDate: endDate ? moment(endDate).endOf('day').toISOString() : null,
                status: status,
            })
            // Upcoming orders don't support status filtering
            retrieveMemberUpcomingOrders({
                limit: LIMIT,
                offset: upcomingOffset,
            })
            hasInitialLoad.current = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated])

    // Reset offset and refetch when date range or category filter changes
    useEffect(() => {
        if (isAuthenticated && viewType === 'History' && hasInitialLoad.current) {
            // Reset offset to 0 when date range or category filter changes
            setHistoryOffset(0)
            shouldScrollToTop.current = true
        }
        // eslint-disable-next-line
    }, [isAuthenticated, startDate, endDate, categoryFilter, viewType])

    // Fetch order history when historyOffset, startDate, endDate, or categoryFilter changes and viewType is History
    useEffect(() => {
        if (isAuthenticated && viewType === 'History' && hasInitialLoad.current) {
            shouldScrollToTop.current = true
            const status = ORDER_HISTORY_FILTER_TO_BACKEND_STATUS[categoryFilter.value] || []
            retrieveMemberOrderHistory({
                limit: LIMIT,
                offset: historyOffset,
                startDate: startDate ? moment(startDate).startOf('day').toISOString() : null,
                endDate: endDate ? moment(endDate).endOf('day').toISOString() : null,
                status: status,
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, historyOffset, viewType, startDate, endDate, categoryFilter])

    // Fetch upcoming orders when upcomingOffset changes and viewType is Upcoming
    // Note: Upcoming orders don't support status or date range filtering
    useEffect(() => {
        if (isAuthenticated && viewType === 'Upcoming' && hasInitialLoad.current) {
            shouldScrollToTop.current = true
            retrieveMemberUpcomingOrders({
                limit: LIMIT,
                offset: upcomingOffset,
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, upcomingOffset, viewType])

    // Update URL query parameter when viewType changes
    useEffect(() => {
        if (viewType === 'Upcoming') {
            setSearchParams({ tab: 'upcoming' }, { replace: true })
        } else {
            setSearchParams({}, { replace: true })
        }
    }, [viewType, setSearchParams])

    // Check if upcoming orders are available
    const isUpcomingEnabled = upcomingOrders && upcomingOrders.length > 0

    // Handle refresh
    const handleRefresh = () => {
        if (viewType === 'History') {
            const status = ORDER_HISTORY_FILTER_TO_BACKEND_STATUS[categoryFilter.value] || []
            retrieveMemberOrderHistory({
                limit: LIMIT,
                offset: historyOffset,
                startDate: startDate ? moment(startDate).startOf('day').toISOString() : null,
                endDate: endDate ? moment(endDate).endOf('day').toISOString() : null,
                status: status,
            })
        } else {
            // Upcoming orders don't support status filtering
            retrieveMemberUpcomingOrders({
                limit: LIMIT,
                offset: upcomingOffset,
            })
        }
    }

    // Handle pagination
    const handlePrevPage = () => {
        if (viewType === 'History') {
            if (historyOffset >= LIMIT) {
                const newOffset = historyOffset - LIMIT
                setHistoryOffset(newOffset)
            }
        } else {
            if (upcomingOffset >= LIMIT) {
                const newOffset = upcomingOffset - LIMIT
                setUpcomingOffset(newOffset)
            }
        }
    }

    const handleNextPage = () => {
        if (viewType === 'History') {
            if (orderHistoryPageInfo?.hasNextPage) {
                const newOffset = historyOffset + LIMIT
                setHistoryOffset(newOffset)
            }
        } else {
            if (upcomingOrdersPageInfo?.hasNextPage) {
                const newOffset = upcomingOffset + LIMIT
                setUpcomingOffset(newOffset)
            }
        }
    }

    // Calculate pagination info based on view type
    const currentPageInfo =
        viewType === 'History' ?
            {
                currentPage: orderHistoryPageInfo ? Math.floor(historyOffset / LIMIT) + 1 : 1,
                totalPages: orderHistoryPageInfo?.totalPages || 1,
                hasPreviousPage: historyOffset >= LIMIT,
                hasNextPage: orderHistoryPageInfo?.hasNextPage || false,
            }
        :   {
                currentPage: upcomingOrdersPageInfo ? Math.floor(upcomingOffset / LIMIT) + 1 : 1,
                totalPages: upcomingOrdersPageInfo?.totalPages || 1,
                hasPreviousPage: upcomingOffset >= LIMIT,
                hasNextPage: upcomingOrdersPageInfo?.hasNextPage || false,
            }

    // Get current orders list based on view type
    // Ensure we only use the correct data source based on viewType
    const currentOrders = useMemo(() => {
        if (viewType === 'History') {
            return orderHistory || []
        } else {
            return upcomingOrders || []
        }
    }, [viewType, orderHistory, upcomingOrders])
    const isLoadingOrders = viewType === 'History' ? orderHistoryLoading : upcomingOrdersLoading
    const ordersError = viewType === 'History' ? orderHistoryError : upcomingOrdersError

    // Scroll to top when data loading completes after pagination
    useEffect(() => {
        if (!isLoadingOrders && shouldScrollToTop.current && hasInitialLoad.current) {
            window.scrollTo({ top: 0, behavior: 'smooth' })
            shouldScrollToTop.current = false
        }
    }, [isLoadingOrders])

    // Format date for display
    const formatDate = dateString => {
        if (!dateString) return ''
        return moment(dateString).format('MMM DD, YYYY')
    }

    // Format datetime for display
    // const formatDateTime = dateString => {
    //     if (!dateString) return ''
    //     return moment(dateString).format('MMM DD, YYYY @ HH:mm')
    // }

    const formatTime = dateString => {
        if (!dateString) return ''
        return moment(dateString).format('HH:mm')
    }

    // Check if all variants in all dishes are completed
    const isOrderCompleted = order => {
        if (!order.dishes || order.dishes.length === 0) return false

        // Check all dishes
        for (const dish of order.dishes) {
            if (!dish.variants || dish.variants.length === 0) return false

            // Check all variants of this dish
            for (const variant of dish.variants) {
                // If any variant doesn't have completedAt, order is not completed
                if (!variant.completedAt) return false
            }
        }

        return true
    }

    // Get order status
    const getOrderStatus = order => {
        // Check if order is completed (all variants have completedAt)
        if (isOrderCompleted(order)) {
            return 'Completed'
        }

        // Check payment status
        if (!order.payment && order.status === 'UNPAID') {
            return 'Unpaid'
        }
        if (order.payment && order.status === 'PAID') {
            return 'Paid'
        }
        if (order.status === 'CANCELLED') {
            return 'Cancelled'
        }

        return 'Processing'
    }

    // Get order status class
    const getOrderStatusClass = order => {
        const status = getOrderStatus(order)
        switch (status) {
            case 'Completed':
                return 'status-completed'
            case 'Paid':
                return 'status-paid'
            case 'Unpaid':
                return 'status-unpaid'
            case 'Processing':
                return 'status-processing'
            default:
                return 'status-default'
        }
    }

    // Get order type
    const getOrderType = order => {
        const isDineIn = order.isDineIn
        if (isDineIn) {
            return 'Dine In'
        } else if (order.pickupScheduledAt) {
            return 'Advance Takeaway'
        } else if (!isDineIn) {
            return 'Takeaway'
        }
        return 'Unknown'
    }

    // Get order type (for display text)
    const getOrderTypeText = order => {
        const isDineIn = order.isDineIn
        if (isDineIn) {
            return `Dine In`
        } else if (order.pickupScheduledAt) {
            return `Advance Takeaway · Pickup @ ${formatTime(order.pickupScheduledAt)}`
        } else if (!isDineIn) {
            return `Takeaway`
        }

        return 'Unknown'
    }

    // Handle navigate to order status page
    const handleCheckStatus = order => {
        const orderNumber = order.number || order.orderId
        navigate(ROUTES.Order.number.replace(':orderNumber', orderNumber))
    }

    // Filter orders based on search term
    // Note: Date range and status filtering are now handled by the backend
    const filteredOrders = useMemo(() => {
        let filtered = currentOrders || []

        // Filter by search term
        if (searchTerm) {
            const lowerSearchTerm = searchTerm.toLowerCase()
            filtered = filtered.filter(order => {
                const searchableText = `
                    ${order.number || ''} 
                    ${order.dishes?.[0]?.vendorName || ''} 
                    ${order.dishes?.map(d => d.name).join(' ') || ''}
                `.toLowerCase()
                return searchableText.includes(lowerSearchTerm)
            })
        }

        return filtered
    }, [currentOrders, searchTerm])

    // Group dishes by cafeStall
    const getDishesByStall = order => {
        if (!order.dishes || order.dishes.length === 0) return []

        const grouped = order.dishes.reduce((acc, dish) => {
            const stallName = dish.cafeStallName || 'Unknown Stall'
            if (!acc[stallName]) {
                acc[stallName] = []
            }
            acc[stallName].push(dish)
            return acc
        }, {})

        const stallGroups = Object.entries(grouped).map(([stallName, dishes]) => ({
            stallName,
            dishes: dishes
                .reduce((acc, dish) => {
                    const existingItem = acc.find(item => item.name === dish.name)
                    const dishQuantity = dish.variants?.[0]?.quantity || 1

                    if (existingItem) {
                        existingItem.quantity += dishQuantity
                    } else {
                        acc.push({
                            name: dish.name,
                            quantity: dishQuantity,
                        })
                    }

                    return acc
                }, [])
                .map(item => `${item.name} x ${item.quantity}`),
        }))

        // Add separator between stall groups
        const result = []
        stallGroups.forEach((stallGroup, index) => {
            result.push(stallGroup)
            if (index < stallGroups.length - 1) {
                result.push({ isSeparator: true })
            }
        })

        return result
    }

    // Get total amount
    const getTotalAmount = order => {
        const amount = order.summary?.finalAmount
        if (amount === 0) return 'Free'
        return amount?.toFixed(2) || '0.00'
    }

    const getServingDate = order => {
        return order.servingDateTime || order.pickupScheduledAt || order.createdOn
    }

    // Handle view details
    const handleViewDetails = order => {
        setSelectedOrder(order)
        setIsDetailModalOpen(true)
    }

    // Handle close modal
    const handleCloseModal = () => {
        setIsDetailModalOpen(false)
        setSelectedOrder(null)
    }

    // Check if order is Advance Takeaway and unpaid
    const isAdvanceTakeawayUnpaid = order => {
        return order.pickupScheduledAt && !order.payment && order.status === 'UNPAID'
    }

    // Handle pay via PayNow
    const handlePayViaPayNow = order => {
        navigate(ROUTES.AdvanceTakeawayPayment.root.replace(':orderId', order.orderId))
    }

    // Handle cancel order
    const handleCancelOrder = async order => {
        // Check if order is cancellable
        if (!order.cancellationInfo?.isCancellable) {
            const deadline = order.cancellationInfo?.cancellationDeadline
            const deadlineText = deadline ? moment(deadline).format('MMM DD, YYYY @ HH:mm') : ''
            const message =
                deadlineText ?
                    `This order cannot be cancelled. The cancellation deadline was ${deadlineText}.`
                :   'This order cannot be cancelled.'
            customToast.error(message, { duration: 5000 })
            return
        }

        // Show confirmation dialog
        const servingDate = formatDate(getServingDate(order))
        const confirmed = await confirm(
            `Are you sure you want to cancel order ORD-${order.number || order.orderId} (${servingDate})? This action cannot be undone.`
        )

        if (!confirmed) {
            return
        }

        // Clear any previous errors
        clearError()

        // Set cancelling order ID
        setCancellingOrderId(order.orderId)

        try {
            await cancelMemberOrder({
                orderId: order.orderId,
                reason: null, // Optional: can add reason input later
            }).unwrap()

            // Refresh upcoming orders after successful cancellation
            // Note: Upcoming orders don't support status filtering
            retrieveMemberUpcomingOrders({
                limit: LIMIT,
                offset: upcomingOffset,
            })
            // Show success message
            customToast.success('Order cancelled successfully.')
        } catch (error) {
            ErrorTrackingAPI.sendError(error, 'MemberHistoryPage', 'cancelOrder')
            // Show error message
            const errorMessage = error || 'Failed to cancel order. Please try again.'
            customToast.error(errorMessage, { duration: 5000 })
        } finally {
            setCancellingOrderId(null)
        }
    }

    const _renderOrderDishes = order => {
        const dishesByStall = getDishesByStall(order)

        if (dishesByStall.length === 0) {
            return <p className="order-items">No items</p>
        }

        return dishesByStall.map((item, index) => {
            if (item.isSeparator) {
                return <div key={`separator-${index}`} className="stall-separator" style={{ height: '8px' }}></div>
            }
            return (
                <div key={index} className="stall-group mb-1">
                    <p className="order-vendor font-weight-bold">{item.stallName}</p>
                    <p className="order-items">{item.dishes.join(', ')}</p>
                </div>
            )
        })
    }

    if (isLoading) {
        return <Loader />
    }

    if (!isAuthenticated || !memberData) {
        return null
    }

    return (
        <div className="member-history-page">
            {cancelOrderLoading && <Loader />}
            <div className="history-header">
                <div className="header-title">
                    <h2>Order History</h2>
                </div>
                <button className="refresh-btn" onClick={handleRefresh} title="Refresh">
                    <i className="bx bx-refresh"></i>
                </button>
            </div>

            <OrderHistoryFilters
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                viewType={viewType}
                setViewType={setViewType}
                categoryFilter={categoryFilter}
                setCategoryFilter={setCategoryFilter}
                categoryOptions={ORDER_HISTORY_FILTER_CATEGORY_OPTIONS}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                isUpcomingEnabled={isUpcomingEnabled}
            />

            <div className="history-content">
                {isLoadingOrders ?
                    <Loader />
                : ordersError ?
                    <div className="error-message">{ordersError}</div>
                : filteredOrders.length === 0 ?
                    <div className="empty-state">
                        <p>No orders found</p>
                    </div>
                :   <div className="order-list">
                        {filteredOrders.map(order => (
                            <div key={order.orderId} className="order-card">
                                <div className="order-card-header">
                                    <div className="order-info">
                                        <h3 className="order-id">ORD-{order.number || order.orderId}</h3>
                                        <p className="order-date">{formatDate(getServingDate(order))}</p>
                                    </div>
                                    <div className={`order-status ${getOrderStatusClass(order)}`}>{getOrderStatus(order)}</div>
                                </div>
                                <p className={`order-type`}>{getOrderTypeText(order)}</p>
                                <div className="order-card-body">
                                    {_renderOrderDishes(order)}
                                    <p className="order-total">${getTotalAmount(order)}</p>
                                </div>
                                <div className="order-card-actions">
                                    <button className="btn-view-details" onClick={() => handleViewDetails(order)}>
                                        View Details
                                    </button>
                                    {order.isToday && (
                                        <button className="btn-check-status" onClick={() => handleCheckStatus(order)}>
                                            Check Status
                                        </button>
                                    )}
                                    {viewType === 'Upcoming' && (
                                        <>
                                            {isAdvanceTakeawayUnpaid(order) ?
                                                <button className="btn-pay-paynow" onClick={() => handlePayViaPayNow(order)}>
                                                    Pay via PayNow
                                                </button>
                                            :   <button
                                                    className="btn-cancel-order"
                                                    onClick={() => handleCancelOrder(order)}
                                                    disabled={
                                                        !order.cancellationInfo?.isCancellable ||
                                                        cancellingOrderId === order.orderId ||
                                                        cancelOrderLoading
                                                    }
                                                    title={
                                                        !order.cancellationInfo?.isCancellable ?
                                                            order.cancellationInfo?.cancellationDeadline ?
                                                                `Cancellation deadline: ${moment(order.cancellationInfo.cancellationDeadline).format('MMM DD, YYYY @ HH:mm')}`
                                                            :   'This order cannot be cancelled'
                                                        :   'Cancel this order'
                                                    }
                                                >
                                                    {cancellingOrderId === order.orderId ? 'Cancelling...' : 'Cancel'}
                                                </button>
                                            }
                                        </>
                                    )}
                                    {/* <button className="btn-reorder">Reorder</button> */}
                                </div>
                            </div>
                        ))}
                    </div>
                }
            </div>

            {currentPageInfo.totalPages > 1 && (
                <div className="pagination">
                    <button disabled={!currentPageInfo.hasPreviousPage} className="pagination-btn" onClick={handlePrevPage}>
                        <i className="bx bx-chevron-left"></i>
                    </button>
                    <span className="pagination-info">
                        Page {currentPageInfo.currentPage} of {currentPageInfo.totalPages}
                    </span>
                    <button disabled={!currentPageInfo.hasNextPage} className="pagination-btn" onClick={handleNextPage}>
                        <i className="bx bx-chevron-right"></i>
                    </button>
                </div>
            )}

            <OrderDetailModal
                isOpen={isDetailModalOpen}
                toggle={handleCloseModal}
                order={selectedOrder}
                getOrderStatus={getOrderStatus}
                getOrderStatusClass={getOrderStatusClass}
                getOrderType={getOrderType}
            />
        </div>
    )
}
